// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — the components. Every printed number in the game lives here and nowhere else.
//
// THE ONE IDEA THE WHOLE GAME IS BUILT ON: a ware's TARIFF is a column of chits printed in
// DESCENDING order, and a sale takes them off the top. So the price of a ware is not a constant the
// designer chose — it is a shared, finite resource that either player can drain, and holding a set
// costs you the difference between the chit at the top today and the chit at the top when you
// finally sell. Nothing else in this file is interesting; that one shape is the product.
//
// THE ARITHMETIC THAT MAKES IT BITE, and it is deliberate rather than decorative:
// every ware has MORE CARDS THAN CHITS. 33 ware cards chase 26 chits, so seven ware cards a round
// are structurally worthless paper. A player who tries to hold everything ends the round holding
// exactly that.
//
//   ware    cards  tariff                 chits  sum  closable in one sale?
//   COPPER    5    8, 6, 5, 4               4     23  yes — 4 of the 5 in the game
//   GLASS     5    7, 6, 5, 3               4     21  yes — 4 of the 5 in the game
//   RESIN     6    6, 5, 4, 3, 3            5     21  yes — 5 of the 6 in the game
//   TIN       8    4, 3, 3, 2, 2, 1         6     15  only with an entire six-card satchel
//   SALT      9    3, 2, 2, 1, 1, 1, 1      7     11  NEVER (7 chits > the 6-card satchel limit)
//
// Every round-end trigger therefore sits on a CONTESTED tariff. There is no cheap uncontestable
// guillotine a player can pull to end a round they happen to be winning — the shortest column still
// needs four of the five copper in the game, which the rival can see you collecting.

/** A card is a ware, or a porter. Porters are not wares and are never sellable. */
export type Ware = 'copper' | 'glass' | 'resin' | 'tin' | 'salt';
export type Card = Ware | 'porter';

/** Printed order. Used for every tie-break in the game, and for column order on screen. */
export const WARES: readonly Ware[] = ['copper', 'glass', 'resin', 'tin', 'salt'] as const;

export const PORTER = 'porter' as const;

export function isWare(c: Card): c is Ware {
  return c !== PORTER;
}

/** How many of each card the deck holds. 33 wares + 9 porters = 42. */
export const CARD_COUNT: Readonly<Record<Card, number>> = {
  copper: 5,
  glass: 5,
  resin: 6,
  tin: 8,
  salt: 9,
  porter: 9,
};

/** The price column, top (dearest) first. A sale takes from the front. */
export const TARIFF: Readonly<Record<Ware, readonly number[]>> = {
  copper: [8, 6, 5, 4],
  glass: [7, 6, 5, 3],
  resin: [6, 5, 4, 3, 3],
  tin: [4, 3, 3, 2, 2, 1],
  salt: [3, 2, 2, 1, 1, 1, 1],
};

/**
 * COPPER, GLASS and RESIN may only be sold two or more at a time. That single restriction is what
 * stops the dearest tariffs from being sliced one chit at a time with no commitment at all.
 */
export const FINE: Readonly<Record<Ware, boolean>> = {
  copper: true,
  glass: true,
  resin: true,
  tin: false,
  salt: false,
};

export const MIN_SALE = (w: Ware): number => (FINE[w] ? 2 : 1);

/**
 * BOUNTIES, and they are FACE UP. There is no face-down value anywhere in this game, which deletes
 * an entire class of bug: a bot cannot leak knowledge of a hidden bonus stack, because there is no
 * hidden bonus stack to know.
 */
export type BountyTier = 0 | 1 | 2;
export const BOUNTY: readonly (readonly number[])[] = [
  [3, 2, 2, 1], // tier I
  [5, 4, 3], //    tier II
  [8, 6, 4], //    tier III
];

/** A bounty needs BOTH: at least three cards sold, and at least ten chits paid. */
export const BOUNTY_MIN_CARDS = 3;
export const BOUNTY_MIN_CHITS = 10;

/**
 * The tier is keyed on CHITS PAID, never on card count — and that is the whole reason the cheapest
 * ware cannot farm bounties. Six SALT (an entire satchel, the largest sale the rules permit of it)
 * pays 3+2+2+1+1+1 = 10, which scrapes tier I at a deliberately terrible rate; five SALT into a
 * half-drained tariff pays 4 and earns nothing at all. A card-count key would have made the most
 * abundant ware the best bounty route, which is a dominant line rather than a decision.
 */
export function bountyTierFor(chitsPaid: number, cards: number): BountyTier | null {
  if (cards < BOUNTY_MIN_CARDS || chitsPaid < BOUNTY_MIN_CHITS) return null;
  if (chitsPaid >= 20) return 2;
  if (chitsPaid >= 15) return 1;
  return 0;
}

export const BOUNTY_NAMES: readonly string[] = ['I', 'II', 'III'];

/** Satchel limit, in WARE cards. Porters live in the gang and never count against it. */
export const SATCHEL_LIMIT = 6;
/** Berths on the trestle. */
export const BERTHS = 5;
/** Awarded at round end to the strictly larger gang. A tie pays nobody. */
export const HIRE_BONUS = 6;
/** Paid to whoever did NOT start the round — the compensation for the second move. */
export const TARE_ALLOWANCE = 2;
/** Empty this many ware tariffs and the round ends on that very ply. */
export const DRY_TO_END = 3;

/** Total printed value in a round: 91 tariff + 38 bounty. Asserted by the audit's fixture. */
export const TARIFF_TOTAL = WARES.reduce((n, w) => n + TARIFF[w].reduce((a, b) => a + b, 0), 0);
export const BOUNTY_TOTAL = BOUNTY.reduce((n, s) => n + s.reduce((a, b) => a + b, 0), 0);

/** The full 42-card deck as a flat list, in printed order. Shuffled by the caller. */
export function buildDeck(): Card[] {
  const out: Card[] = [];
  for (const w of WARES) for (let i = 0; i < CARD_COUNT[w]; i++) out.push(w);
  for (let i = 0; i < CARD_COUNT.porter; i++) out.push(PORTER);
  return out;
}

export const DECK_SIZE = buildDeck().length;

/** Display names, and the glyph that carries identity when colour cannot (five colours can never be
 *  mutually 3:1 — see contrast.test.ts for the arithmetic). */
export const WARE_NAME: Readonly<Record<Ware, string>> = {
  copper: 'Copper',
  glass: 'Glass',
  resin: 'Resin',
  tin: 'Tin',
  salt: 'Salt',
};
